import { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';

/**
 * useDraftPublish — generic hook that manages the
 *   getState / saveDraft / publish / revert / previewToken
 * lifecycle used by all Phase 3 website resources (site settings, pages, ...).
 *
 * It isn't coupled to site-settings specifically; pass the matching API methods
 * for whichever resource you're editing.
 *
 * Features:
 *   - Unmount-safe (no setState after unmount, cleans up in-flight requests).
 *   - Toast success on publish/revert, surfaces axios errors on every op.
 *   - `saveDraft(partial)` merges partial into the current draft on the server
 *     side (backend is responsible for the deep merge — we just PUT).
 *   - `openPreview` fetches a short-lived token then opens the preview URL
 *     in a new tab using either the caller-provided `buildPreviewUrl` or a
 *     sensible default that reads the tenant slug from localStorage.
 */

// Default preview URL builder — used when the caller doesn't override it.
// Reads `tenant` from localStorage (stored as JSON `{ slug, ... }`) and builds
// the listechs.com subdomain URL with a `preview` query param.
function defaultBuildPreviewUrl(tokenPayload) {
  let slug = '';
  try {
    const raw = localStorage.getItem('tenant');
    if (raw) {
      const parsed = JSON.parse(raw);
      slug = parsed?.slug || '';
    }
  } catch (_) {
    slug = '';
  }
  const token = tokenPayload?.token || tokenPayload?.previewToken || '';
  const base = slug
    ? `https://${slug}.sites.listechs.com/`
    : 'https://sites.listechs.com/';
  return `${base}?preview=${encodeURIComponent(token)}`;
}

export default function useDraftPublish({
  getState,
  saveDraft: saveDraftApi,
  publish: publishApi,
  revert: revertApi,
  previewToken: previewTokenApi,
  buildPreviewUrl,
  onStateChange,
} = {}) {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [reverting, setReverting] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [error, setError] = useState(null);

  const mountedRef = useRef(true);
  const onStateChangeRef = useRef(onStateChange);
  const buildPreviewUrlRef = useRef(buildPreviewUrl);

  useEffect(() => { onStateChangeRef.current = onStateChange; }, [onStateChange]);
  useEffect(() => { buildPreviewUrlRef.current = buildPreviewUrl; }, [buildPreviewUrl]);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Helper: unwrap { success, data } envelope used across the backend.
  const unwrap = (resp) => {
    const body = resp?.data;
    return body?.data ?? body ?? null;
  };

  const applyState = useCallback((next) => {
    if (!mountedRef.current) return;
    setState(next);
    if (typeof onStateChangeRef.current === 'function') {
      try { onStateChangeRef.current(next); } catch (_) { /* ignore */ }
    }
  }, []);

  const extractErr = (err, fallback) => {
    const msg =
      err?.response?.data?.message ||
      err?.response?.data?.error ||
      err?.message ||
      fallback;
    return msg;
  };

  const refetch = useCallback(async () => {
    if (typeof getState !== 'function') return;
    if (mountedRef.current) {
      setLoading(true);
      setError(null);
    }
    try {
      const resp = await getState();
      const data = unwrap(resp);
      applyState(data);
      return data;
    } catch (err) {
      const msg = extractErr(err, 'تعذّر تحميل الإعدادات');
      if (mountedRef.current) setError(msg);
      toast.error(msg);
      return null;
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [getState, applyState]);

  // Fetch once on mount.
  useEffect(() => {
    refetch();
  }, [refetch]);

  const saveDraft = useCallback(async (partial) => {
    if (typeof saveDraftApi !== 'function') return null;
    if (mountedRef.current) {
      setSaving(true);
      setError(null);
    }
    try {
      const resp = await saveDraftApi(partial || {});
      const data = unwrap(resp);
      applyState(data);
      return data;
    } catch (err) {
      const msg = extractErr(err, 'تعذّر حفظ المسودة');
      if (mountedRef.current) setError(msg);
      toast.error(msg);
      return null;
    } finally {
      if (mountedRef.current) setSaving(false);
    }
  }, [saveDraftApi, applyState]);

  const publish = useCallback(async () => {
    if (typeof publishApi !== 'function') return null;
    if (mountedRef.current) {
      setPublishing(true);
      setError(null);
    }
    try {
      const resp = await publishApi();
      const data = unwrap(resp);
      applyState(data);
      toast.success('تم نشر التغييرات بنجاح');
      return data;
    } catch (err) {
      const msg = extractErr(err, 'تعذّر النشر');
      if (mountedRef.current) setError(msg);
      toast.error(msg);
      return null;
    } finally {
      if (mountedRef.current) setPublishing(false);
    }
  }, [publishApi, applyState]);

  const revert = useCallback(async () => {
    if (typeof revertApi !== 'function') return null;
    // eslint-disable-next-line no-alert
    const ok = window.confirm(
      'سيتم التخلص من جميع التعديلات غير المنشورة والرجوع إلى آخر نسخة منشورة. هل أنت متأكد؟'
    );
    if (!ok) return null;

    if (mountedRef.current) {
      setReverting(true);
      setError(null);
    }
    try {
      const resp = await revertApi();
      const data = unwrap(resp);
      applyState(data);
      toast.success('تم التراجع إلى آخر نسخة منشورة');
      return data;
    } catch (err) {
      const msg = extractErr(err, 'تعذّر التراجع');
      if (mountedRef.current) setError(msg);
      toast.error(msg);
      return null;
    } finally {
      if (mountedRef.current) setReverting(false);
    }
  }, [revertApi, applyState]);

  const openPreview = useCallback(async () => {
    if (typeof previewTokenApi !== 'function') {
      toast.error('المعاينة غير متاحة حالياً');
      return null;
    }
    if (mountedRef.current) {
      setPreviewLoading(true);
      setError(null);
    }
    try {
      const resp = await previewTokenApi();
      const data = unwrap(resp);
      const builder = buildPreviewUrlRef.current || defaultBuildPreviewUrl;
      const url = builder(data || {});
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
      return data;
    } catch (err) {
      const msg = extractErr(err, 'تعذّر فتح المعاينة');
      if (mountedRef.current) setError(msg);
      toast.error(msg);
      return null;
    } finally {
      if (mountedRef.current) setPreviewLoading(false);
    }
  }, [previewTokenApi]);

  return {
    state,
    loading,
    saving,
    publishing,
    reverting,
    previewLoading,
    error,
    saveDraft,
    publish,
    revert,
    openPreview,
    refetch,
  };
}
